/**
 * 🔍 SCAN-INVENTORY-2026-05-16 — QuickAssignBarcodeDialog
 *
 * Cuando se escanea/teclea un código de barras que NO existe en la DB, este dialog
 * permite resolver el caso sin abandonar el flujo de conteo:
 *
 *   1) [Crear nuevo insumo]    — redirige a tab "Insumos" con state pre-llenado
 *   2) [Asignar a existente]   — busca por nombre, parchea barcode, vuelve al flujo
 *   3) [Cancelar]              — cierra y vuelve al scanner
 *
 * Props:
 *   - open / onOpenChange
 *   - barcode       — string detectado (UPC, EAN, CODE-128, etc.)
 *   - scanMode      — 'products' | 'ingredients' | 'mixed' (define qué se puede asignar)
 *   - onAssigned(barcode) — callback cuando se asignó correctamente, para re-disparar lookup
 *   - onCreateNew(barcode) — callback para "crear nuevo" (typically navega a Insumos)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertCircle, PlusCircle, LinkIcon, X, Search, Loader2, Package, Boxes, CheckCircle2,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { productsAPI, ingredientsAPI } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('pos_token')}` });

export default function QuickAssignBarcodeDialog({
  open,
  onOpenChange,
  barcode,
  scanMode = 'mixed',
  onAssigned,
  onCreateNew,
}) {
  const [step, setStep] = useState('choose'); // 'choose' | 'assign-search'
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ products: [], ingredients: [] });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setStep('choose');
      setSearchTerm('');
      setResults({ products: [], ingredients: [] });
      setAssigning(false);
    }
  }, [open]);

  const allowProducts = scanMode === 'products' || scanMode === 'mixed';
  const allowIngredients = scanMode === 'ingredients' || scanMode === 'mixed';

  /* ───── Search (debounced inline) ───── */
  useEffect(() => {
    if (step !== 'assign-search') return;
    const term = (searchTerm || '').trim();
    if (term.length < 2) {
      setResults({ products: [], ingredients: [] });
      return;
    }
    let cancelled = false;
    const tid = setTimeout(async () => {
      setSearching(true);
      try {
        const reqs = [];
        if (allowProducts) {
          reqs.push(
            axios.get(`${API}/products`, { headers: authHdr(), params: { include_inactive: false } })
              .then(r => (r.data || []).filter(p =>
                (p.name || '').toLowerCase().includes(term.toLowerCase())
              ).slice(0, 8))
              .catch(() => [])
          );
        } else {
          reqs.push(Promise.resolve([]));
        }
        if (allowIngredients) {
          reqs.push(
            axios.get(`${API}/ingredients`, { headers: authHdr() })
              .then(r => (r.data || []).filter(i =>
                (i.name || '').toLowerCase().includes(term.toLowerCase())
              ).slice(0, 8))
              .catch(() => [])
          );
        } else {
          reqs.push(Promise.resolve([]));
        }
        const [products, ingredients] = await Promise.all(reqs);
        if (!cancelled) setResults({ products, ingredients });
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [searchTerm, step, allowProducts, allowIngredients]);

  /* ───── Assign barcode to existing item ───── */
  const assignTo = async (item, isIngredient) => {
    if (!barcode || !item?.id) return;
    setAssigning(true);
    try {
      if (isIngredient) {
        await ingredientsAPI.update(item.id, { barcode });
      } else {
        await productsAPI.update(item.id, { barcode });
      }
      notify.success(`Código asignado a ${item.name}`, {
        description: `Barcode: ${barcode}`,
      });
      try { onAssigned?.(barcode); } catch {}
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      notify.error('No se pudo asignar el código', {
        description: typeof detail === 'string' ? detail : (detail?.message || e.message),
      });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Código no registrado
          </DialogTitle>
          <DialogDescription>
            El código <strong className="font-mono text-foreground">{barcode}</strong> no
            está asignado a ningún {allowProducts && allowIngredients ? 'producto o insumo' :
              allowProducts ? 'producto' : 'insumo'}. ¿Qué quieres hacer?
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' && (
          <div className="space-y-2 py-2" data-testid="quick-assign-choose">
            <button
              type="button"
              onClick={() => { try { onCreateNew?.(barcode); } catch {} onOpenChange?.(false); }}
              className="w-full flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/40 active:scale-[0.99] transition text-left"
              data-testid="quick-assign-create-new"
            >
              <PlusCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-foreground">Crear insumo nuevo</div>
                <div className="text-xs text-muted-foreground">
                  Abre el editor de insumos con el código pre-asignado. Tendrás que completar
                  nombre, unidad, costo y guardar.
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStep('assign-search')}
              className="w-full flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/40 active:scale-[0.99] transition text-left"
              data-testid="quick-assign-link-existing"
            >
              <LinkIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-foreground">Asignar a uno existente</div>
                <div className="text-xs text-muted-foreground">
                  Busca por nombre el insumo/producto y vincula este código de barras a él
                  permanentemente. La próxima vez que lo escanees aparecerá directo.
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="w-full flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/40 active:scale-[0.99] transition text-left"
              data-testid="quick-assign-cancel-choice"
            >
              <X className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-foreground">Cancelar</div>
                <div className="text-xs text-muted-foreground">
                  Volver al escáner sin hacer nada.
                </div>
              </div>
            </button>
          </div>
        )}

        {step === 'assign-search' && (
          <div className="space-y-3 py-1" data-testid="quick-assign-search-step">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre…"
                className="pl-8 h-10"
                data-testid="quick-assign-search-input"
              />
            </div>

            {searchTerm.trim().length < 2 && (
              <p className="text-xs text-muted-foreground text-center py-3">
                Escribe al menos 2 letras para buscar.
              </p>
            )}

            {searching && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground text-xs py-3">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando…
              </div>
            )}

            {!searching && searchTerm.trim().length >= 2 && (
              <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="quick-assign-results">
                {results.products.length === 0 && results.ingredients.length === 0 && (
                  <div className="text-center text-xs text-muted-foreground py-6">
                    Ningún resultado. Prueba con otro término o crea uno nuevo.
                  </div>
                )}
                {results.products.map((p) => (
                  <button
                    key={`p-${p.id}`}
                    type="button"
                    disabled={assigning}
                    onClick={() => assignTo(p, false)}
                    className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-muted/50 active:bg-muted disabled:opacity-50"
                    data-testid={`quick-assign-pick-product-${p.id}`}
                  >
                    <Package className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        Producto · {p.category_name || 'sin categoría'}
                        {p.barcode ? ` · ya tiene código ${p.barcode}` : ''}
                      </div>
                    </div>
                    {p.barcode && <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" title="Ya tiene barcode; será reemplazado" />}
                  </button>
                ))}
                {results.ingredients.map((i) => (
                  <button
                    key={`i-${i.id}`}
                    type="button"
                    disabled={assigning}
                    onClick={() => assignTo(i, true)}
                    className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-muted/50 active:bg-muted disabled:opacity-50"
                    data-testid={`quick-assign-pick-ingredient-${i.id}`}
                  >
                    <Boxes className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{i.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        Insumo · {i.category || 'general'} · {i.unit || 'unidad'}
                        {i.barcode ? ` · ya tiene código ${i.barcode}` : ''}
                      </div>
                    </div>
                    {i.barcode && <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" title="Ya tiene barcode; será reemplazado" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-3 flex-col sm:flex-row">
          {step === 'assign-search' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('choose')}
              className="min-h-[40px]"
              data-testid="quick-assign-back-btn"
            >
              ← Volver
            </Button>
          )}
          {step === 'choose' ? null : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              className="min-h-[40px]"
              data-testid="quick-assign-close-btn"
              disabled={assigning}
            >
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
